import { getPremiumTtsClientId } from "./premiumTts.js";

const apiBaseUrl = import.meta.env.VITE_SIELPA_PLUS_API_BASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const appVersion = import.meta.env.VITE_SIELPA_APP_VERSION || import.meta.env.VITE_APP_VERSION || "";

export const PLUS_PLANS = {
  monthly: {
    id: "monthly",
    label: "Monthly",
    price: "¥300/month",
    lookupKey: import.meta.env.VITE_STRIPE_PLUS_MONTHLY_LOOKUP_KEY || "sielpa_plus_monthly"
  },
  yearly: {
    id: "yearly",
    label: "Yearly",
    price: "¥2,400/year",
    lookupKey: import.meta.env.VITE_STRIPE_PLUS_YEARLY_LOOKUP_KEY || "sielpa_plus_yearly"
  }
};

function normalizedApiBaseUrl() {
  return String(apiBaseUrl || "").replace(/\/$/, "");
}

export function isBillingConfigured() {
  return Boolean(apiBaseUrl && anonKey);
}

const BILLING_HISTORY_KEY = "sielpa-plus:has-billing:v1";
const STATUS_CACHE_KEY = "sielpa-plus:status-cache:v1";
const STATUS_CACHE_TTL_MS = 10 * 60 * 1000;

// Tracks whether this device has ever had a Plus subscription or Stripe customer.
// Brand-new / anonymous visitors (the bulk of page loads, including bots) have no
// history, so the app can default them to "free" without hitting the backend.
export function hasLocalBillingHistory() {
  try {
    return window.localStorage.getItem(BILLING_HISTORY_KEY) === "true";
  } catch {
    return false;
  }
}

export function markBillingHistory() {
  try {
    window.localStorage.setItem(BILLING_HISTORY_KEY, "true");
  } catch {
    // Storage is optional; the in-memory flow still works for the session.
  }
}

function readCachedStatus() {
  try {
    const raw = window.sessionStorage.getItem(STATUS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > STATUS_CACHE_TTL_MS) return null;
    return parsed.status || null;
  } catch {
    return null;
  }
}

function writeCachedStatus(status) {
  try {
    window.sessionStorage.setItem(STATUS_CACHE_KEY, JSON.stringify({ status, ts: Date.now() }));
  } catch {
    // Caching is best-effort; a miss just means another fetch later.
  }
}

let inFlightStatus = null;

function getCurrentAppUrl(status) {
  const url = new URL(window.location.href);
  url.searchParams.set("sielpa_plus", status);
  if (!url.hash) url.hash = "#/";
  return url.toString();
}

function createBillingError(code, message, data = {}) {
  const error = new Error(message);
  error.code = code;
  error.data = data;
  return error;
}

async function postBillingEndpoint(endpoint, payload) {
  if (!isBillingConfigured()) {
    throw createBillingError(
      "billing_not_configured",
      "SielpA Plus checkout is not connected yet. Add the Supabase function URL and anon key to the React environment."
    );
  }

  let response;
  try {
    response = await fetch(`${normalizedApiBaseUrl()}/${endpoint}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${anonKey}`,
        apikey: anonKey,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        app: "sielpa",
        clientId: getPremiumTtsClientId(),
        ...(appVersion ? { appVersion } : {}),
        ...payload
      })
    });
  } catch (error) {
    throw createBillingError(
      "billing_network_error",
      "SielpA Plus checkout could not connect. The payment backend may not be deployed yet.",
      { cause: error }
    );
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    throw createBillingError(
      data.code || (response.status === 404 ? "billing_endpoint_missing" : "billing_request_failed"),
      data.message || "SielpA Plus checkout is not ready yet.",
      data
    );
  }

  return data;
}

export async function createCheckoutSession({ interval = "monthly", source = "app", ...trace } = {}) {
  markBillingHistory();
  const plan = PLUS_PLANS[interval] || PLUS_PLANS.monthly;
  const data = await postBillingEndpoint("create-plus-checkout", {
    plan: "sielpa_plus",
    interval: plan.id,
    priceLookupKey: plan.lookupKey,
    source,
    ...trace,
    successUrl: getCurrentAppUrl("success"),
    cancelUrl: getCurrentAppUrl("canceled")
  });

  const url = data.url || data.checkoutUrl;
  if (!url) {
    throw createBillingError("checkout_url_missing", "Checkout opened, but Stripe did not return a checkout URL.", data);
  }

  return { ...data, url, plan };
}

export async function createCustomerPortalSession({ source = "app" } = {}) {
  markBillingHistory();
  const data = await postBillingEndpoint("create-plus-portal", {
    source,
    returnUrl: getCurrentAppUrl("portal")
  });

  const url = data.url || data.portalUrl;
  if (!url) {
    throw createBillingError("portal_url_missing", "The billing portal did not return a URL.", data);
  }

  return { ...data, url };
}

export async function fetchPlusStatus({ source = "app", force = false } = {}) {
  if (!force) {
    const cached = readCachedStatus();
    if (cached) return cached;
    if (inFlightStatus) return inFlightStatus;
  }

  const run = (async () => {
    const data = await postBillingEndpoint("get-plus-status", { source });
    const status = {
      ...data,
      isPlus: Boolean(data.isPlus || data.tier === "plus" || data.entitlements?.plus),
      isAdFree: Boolean(data.isAdFree || data.isPlus || data.tier === "plus" || data.entitlements?.adFree),
      hasBillingCustomer: Boolean(data.hasBillingCustomer)
    };
    if (status.isPlus || status.hasBillingCustomer) {
      markBillingHistory();
    }
    writeCachedStatus(status);
    return status;
  })();

  if (!force) {
    inFlightStatus = run;
  }
  try {
    return await run;
  } finally {
    if (inFlightStatus === run) inFlightStatus = null;
  }
}
